import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatExpansionModule, MatExpansionPanel } from '@angular/material/expansion';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatChipsModule } from '@angular/material/chips';

import { NotificationService } from '@shared/services/notification.service';
import { UnitSelectorComponent } from '@shared/components/unit-selector/unit-selector.component';
import { OptionalFieldComponent } from '@shared/components/optional-field/optional-field.component';
import { LegacyUser } from '../../models/legacy-user.model';
import { FilterData, UserMigrationRequest } from '../../models/filter-data.model';
import { LegacyUserService } from '@features/user/services/legacy-user.service';

// Constants for default filter data
const DEFAULT_FILTER_DATA: FilterData = {
  documentTypes: [],
  units: [],
  ministries: [],
  assignments: [],
  appointments: [],
  areas: [],
  modules: [],
  countries: [],
  nationalities: []
};

@Component({
  selector: 'app-user-migration-detail',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatProgressBarModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatExpansionModule,
    MatTooltipModule,
    MatChipsModule,
    UnitSelectorComponent,
    OptionalFieldComponent,
  ],
  templateUrl: './user-migration-detail.component.html',
  styleUrl: './user-migration-detail.component.scss'
})
export class UserMigrationDetailComponent implements OnInit, OnDestroy {
  @ViewChild('personalPanel') personalPanel?: MatExpansionPanel;
  @ViewChild('membershipPanel') membershipPanel?: MatExpansionPanel;

  migrationForm!: FormGroup;
  legacyUser: LegacyUser | null = null;
  filterData: FilterData | null = null;
  isLoading = false;
  isMigrating = false;

  private destroy$ = new Subject<void>();

  /**
   * Get safe filter data with default empty arrays
   */
  get safeFilterData(): FilterData {
    return this.filterData || DEFAULT_FILTER_DATA;
  }

  /**
   * Get institution FormGroup
   */
  get institutionForm(): FormGroup {
    return this.migrationForm.get('institution') as FormGroup;
  }

  /**
   * Get appointments FormArray
   */
  get appointmentsArray(): FormArray {
    return this.institutionForm?.get('appointments') as FormArray;
  }

  /**
   * Get assignments FormArray
   */
  get assignmentsArray(): FormArray {
    return this.institutionForm?.get('nominations') as FormArray;
  }

  /**
   * Check if personal info section is valid
   */
  get isPersonalInfoValid(): boolean {
    const fields = ['username', 'firstName', 'lastName', 'email', 'documentType', 'documentNumber'];
    return fields.every(field => this.migrationForm?.get(field)?.valid);
  }

  /**
   * Check if community section is valid
   */
  get isCommunityValid(): boolean {
    return this.institutionForm?.get('unit')?.valid ?? false;
  }

  /**
   * Check if entire church membership section is valid
   */
  get isChurchMembershipValid(): boolean {
    return this.institutionForm?.valid ?? false;
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private legacyUserService: LegacyUserService,
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef
  ) {
    this.initializeMigrationForm();
  }

  ngOnInit(): void {
    this.loadInitialData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Load initial data from route and services
   */
  private loadInitialData(): void {
    this.isLoading = true;

    const email = this.route.snapshot.paramMap.get('email');

    if (!email) {
      this.notificationService.warning('Email no especificado');
      this.router.navigate(['/users/migration']);
      return;
    }

    Promise.all([
      this.legacyUserService.getFilterData().toPromise(),
      this.legacyUserService.getLegacyUserByEmail(email).toPromise()
    ])
      .then(([filterData, legacyUser]) => {
        if (!filterData || !legacyUser) {
          throw new Error('No se pudo cargar los datos');
        }

        this.filterData = filterData;
        this.legacyUser = legacyUser as unknown as LegacyUser;

        this.populateFormWithLegacyData();
        this.isLoading = false;
        this.cdr.markForCheck();
      })
      .catch((error) => {
        console.error('Error loading data:', error);
        this.notificationService.error(
          'Error cargando los datos del usuario: ' + (error?.error?.detail || error.message)
        );
        this.isLoading = false;
        this.router.navigate(['/users/migration']);
        this.cdr.markForCheck();
      });
  }

  /**
   * Initialize the migration form structure
   */
  private initializeMigrationForm(): void {
    this.migrationForm = this.fb.group({
      username: ['', Validators.required],
      email: ['', Validators.required],
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      documentType: [null],
      documentNumber: [''],
      institution: this.createInstitutionFormGroup()
    });

    this.setupDocumentTypeValidation();
  }

  /**
   * Create institution form group with nested FormArrays
   */
  private createInstitutionFormGroup(): FormGroup {
    return this.fb.group({
      unit: [null, Validators.required],
      ministry: [null],
      ministryUnit: [null],
      appointments: this.fb.array([]),
      nominations: this.fb.array([])
    });
  }

  /**
   * Setup conditional validation for document type
   */
  private setupDocumentTypeValidation(): void {
    this.migrationForm.get('documentNumber')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => this.updateDocumentTypeValidation(value));

    // Initial validation check
    const documentNumberValue = this.migrationForm.get('documentNumber')?.value;
    if (documentNumberValue?.trim()) {
      this.migrationForm.get('documentType')?.setValidators(Validators.required);
      this.migrationForm.get('documentType')?.updateValueAndValidity({ emitEvent: false });
    }
  }

  /**
   * Update document type validation based on document number
   */
  private updateDocumentTypeValidation(value: string): void {
    const documentTypeControl = this.migrationForm.get('documentType');
    if (value?.trim()) {
      documentTypeControl?.setValidators(Validators.required);
    } else {
      documentTypeControl?.clearValidators();
    }
    documentTypeControl?.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * Populate form with legacy user data
   */
  private populateFormWithLegacyData(): void {
    if (!this.legacyUser) return;

    const suggestedUsername = this.legacyUser.firstName.split(' ')[0].toLowerCase() + '.' + this.legacyUser.lastName.split(' ')[0].toLowerCase();

    this.migrationForm.patchValue({
      username: suggestedUsername,
      email: this.legacyUser.email,
      firstName: this.legacyUser.firstName,
      lastName: this.legacyUser.lastName,
      documentNumber: this.legacyUser.documentNumber,
    });

    // Mark fields as touched so validation messages appear
    this.migrationForm.get('username')?.markAsTouched();
    this.migrationForm.get('email')?.markAsTouched();
    this.migrationForm.get('firstName')?.markAsTouched();
    this.migrationForm.get('lastName')?.markAsTouched();
    this.migrationForm.get('documentNumber')?.markAsTouched();
  }

  /**
   * Submit the migration request to the backend
   */
  onMigrate(): void {
    if (!this.migrationForm.valid || !this.legacyUser) {
      this.migrationForm.markAllAsTouched();
      const personalFields = ['username', 'email', 'firstName', 'lastName', 'documentType', 'documentNumber'];
      if (personalFields.some(f => this.migrationForm.get(f)?.invalid)) {
        this.personalPanel?.open();
      }
      if (this.institutionForm?.invalid) {
        this.membershipPanel?.open();
      }
      this.cdr.markForCheck();
      return;
    }

    const request = this.buildMigrationRequest();
    this.submitMigration(request);
  }

  /**
   * Build migration request from form data
   */
  private buildMigrationRequest(): UserMigrationRequest {
    const formValue = this.migrationForm.getRawValue();

    // Extract appointments from FormArray
    const ministryAppointments = this.appointmentsArray?.value?.map((item: any) => ({
      unitId: item.unitId || null,
      appointmentId: item.appointmentId
    })) || [];

    // Extract assignments from FormArray
    const assignments = this.assignmentsArray?.value?.map((item: any) => ({
      unitId: item.unitId || null,
      assignmentId: item.nominationId   // nominationId is the form field name, assignmentId is what the API expects
    })) || [];

    // Build ministry object only if ministry is selected
    const ministry = formValue.institution.ministry ? {
      ministryId: formValue.institution.ministry,
      unitId: formValue.institution.ministryUnit || null
    } : null;

    return {
      email: this.legacyUser!.email,
      firstName: formValue.firstName,
      lastName: formValue.lastName,
      documentType: formValue.documentType || 0,
      documentNumber: formValue.documentNumber || '',
      unitId: formValue.institution.unit,
      ministry: ministry,
      ministryAppointments: ministryAppointments,
      assignments: assignments
    };
  }

  /**
   * Submit migration request to the backend
   */
  private submitMigration(request: UserMigrationRequest): void {
    this.isMigrating = true;
    this.cdr.markForCheck();

    this.legacyUserService.migrateUser(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => this.handleMigrationSuccess(response),
        error: (error) => this.handleMigrationError(error)
      });
  }

  /**
   * Handle successful migration response
   */
  private handleMigrationSuccess(response: any): void {
    this.isMigrating = false;

    if (response.success) {
      this.notificationService.success(response.message);
      setTimeout(() => {
        this.router.navigate(['/users/migration']);
      }, 2000);
    } else {
      const errorMessage = response.message || 'Error en la migración';
      const errorDetails = response.errors?.join(', ') || response.errorMessage || '';
      this.notificationService.error(
        `${errorMessage}${errorDetails ? ': ' + errorDetails : ''}`
      );
    }

    this.cdr.markForCheck();
  }

  /**
   * Handle migration error
   */
  private handleMigrationError(error: any): void {
    this.isMigrating = false;
    const errorMessage = error.error?.detail || 'Error conectando con el servidor';
    this.notificationService.error(`Error: ${errorMessage}`);
    this.cdr.markForCheck();
  }

  /**
   * Cancel and go back to list
   */
  onCancel(): void {
    this.router.navigate(['/users/migration']);
  }

  /**
   * Add a new appointment to the form array
   */
  addAppointment(): void {
    this.appointmentsArray.push(this.createAppointmentGroup());
  }

  /**
   * Remove an appointment from the form array
   */
  removeAppointment(index: number): void {
    this.appointmentsArray.removeAt(index);
  }

  /**
   * Add a new assignment to the form array
   */
  addAssignment(): void {
    this.assignmentsArray.push(this.createAssignmentGroup());
  }

  /**
   * Remove an assignment from the form array
   */
  removeAssignment(index: number): void {
    this.assignmentsArray.removeAt(index);
  }

  /**
   * Copy community unit to appointment
   */
  copyUnitToAppointment(index: number): void {
    const communityUnit = this.institutionForm?.get('unit')?.value;
    if (communityUnit) {
      this.appointmentsArray.at(index).patchValue({ unitId: communityUnit });
      this.cdr.markForCheck();
    }
  }

  /**
   * Copy community unit to assignment
   */
  copyUnitToAssignment(index: number): void {
    const communityUnit = this.institutionForm?.get('unit')?.value;
    if (communityUnit) {
      this.assignmentsArray.at(index).patchValue({ unitId: communityUnit });
      this.cdr.markForCheck();
    }
  }

  /**
   * Create a single appointment form group
   */
  private createAppointmentGroup(): FormGroup {
    return this.fb.group({
      appointmentId: [null, Validators.required],
      unitId: [null]
    });
  }

  /**
   * Create a single assignment form group
   */
  private createAssignmentGroup(): FormGroup {
    return this.fb.group({
      nominationId: [null, Validators.required],
      unitId: [null]
    });
  }

  /**
   * Get appointment name by ID
   */
  getAppointmentName(appointmentId: any): string {
    if (!appointmentId) return 'Sin encargo';
    const appointment = this.safeFilterData.appointments.find(a => a.id === appointmentId);
    return appointment?.name || 'Encargo desconocido';
  }

  /**
   * Get unit name by ID
   */
  getUnitName(unitId: any): string {
    if (!unitId) return '';
    const unit = this.safeFilterData.units.find(u => u.id === unitId);
    return unit?.name || 'Unidad desconocida';
  }
}
